import "./styles.css";

import {
  Button,
  Col,
  Dropdown,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
  Typography,
} from "antd";
import { FilterValue, SorterResult } from "antd/lib/table/interface";
import { useEffect, useRef, useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { PlusCircleTwoTone } from "@ant-design/icons";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import {
  adminListAsync,
  updateAdminListValue,
} from "../../../features/admin/adminListSlice";
import {
  deleteAdmin,
  getAdminsExcel,
  impersonateAdminLogin,
  patchAdminPassword,
  getVoucherPreview,
} from "../../../services/adminApi";
import { createAdminWallet } from "../../../services/adminWalletApi";
import {
  AdminWalletConcept,
  AdminWalletDeposit,
  PasswordRegexExpression,
  PasswordRequirementText,
} from "../../../shared/constants/Resources";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import AdminDTO from "../../../shared/dtos/adminDTO";
import AdminWalletDTO from "../../../shared/dtos/adminWalletDTO";
import ContainerLayout from "../../common/containerLayout";
import AdminSwitch from "./utils/adminSwitch";

const { Text } = Typography;

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters: Record<string, FilterValue | null>;
}
const { Search } = Input;
const { Column } = Table;

const AdminList = () => {
  const dispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isLoadingExcel, setIsLoadingExcel] = useState(false);
  const [isVoucherPreviewOpen, setIsVoucherPreviewOpen] = useState(false);
  const [voucherPreviewHtml, setVoucherPreviewHtml] = useState("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [ignoreFetch, setIgnoreFetch] = useState(false);
  const [adminId, setAdminId] = useState<number | null>(null);
  const [data, setData] = useState(new Array<AdminDTO>());
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const title = "Administradores";
  const loading = useAppSelector((state: RootState) => state.adminList.loading);

  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const [tableParams, setTableParams] = useState<TableParams>({
    filters: {
      active: [true],
    },
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  useEffect(() => {
    dispatch(updateAdminListValue());
  }, [dispatch]);

  const fetchData = () => {
    dispatch(
      adminListAsync({
        page: tableParams.pagination?.current || 1,
        take: tableParams.pagination?.pageSize || 10,
        search: searchText,
      }),
    ).then(({ payload }: any) => {
      setData(payload.adminDtos);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: payload.total,
        },
      });
    });

    setIgnoreFetch(true);
  };

  useEffect(() => {
    if (!ignoreFetch) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ignoreFetch, JSON.stringify(tableParams), searchText]);

  const handleAdminType = (adminTypeId: number) => {
    switch (adminTypeId) {
      case 1:
        return "Super Admin";
      case 2:
        return "Admin";
      case 3:
        return "Operador";
      default:
        return "Admin";
    }
  };

  const handleIsActive = (active: any) => {
    switch (active) {
      case true:
        return "Si";
      case false:
        return "No";
      default:
        return "No";
    }
  };

  const handleUpdatePassword = async (values: any) => {
    const { password } = values;
    if (!adminId || !password) return;

    setConfirmLoading(true);
    await patchAdminPassword(adminId, password);
    setConfirmLoading(false);
    passwordForm.resetFields();
    setAdminId(null);
    setShowPasswordInput(!showPasswordInput);
  };

  const handleOk = async (values: any) => {
    const { amount, concept } = values;
    if (!adminId || !amount || !concept) return;

    setConfirmLoading(true);
    const adminWallet: AdminWalletDTO = {
      adminId,
      amount,
      concept,
      transactionType: AdminWalletDeposit,
    };
    await createAdminWallet(adminId, adminWallet);
    setConfirmLoading(false);
    form.resetFields();
    setAdminId(null);
    setIsModalOpen(!isModalOpen);
  };

  const handleExcelExport = async () => {
    setIsLoadingExcel(true);
    const result = await getAdminsExcel();
    const link = document.createElement("a");
    link.href = result;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsLoadingExcel(false);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<AdminDTO> | SorterResult<AdminDTO>[],
  ) => {
    setIgnoreFetch(false);
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  const onSearch = (value: string) => {
    setIgnoreFetch(false);
    setSearchText(value);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    });
  };

  const dropdownItem = (
    text: string,
    record: AdminDTO,
    recordValue: keyof AdminDTO,
  ) => (
    <Row justify="space-between" style={{ padding: 5 }}>
      <Col style={{ marginRight: 10 }}>{text}</Col>
      <AdminSwitch
        adminId={record.id}
        switchValue={record[recordValue] as boolean}
        setIgnoreFetch={setIgnoreFetch}
        switchType={recordValue}
      />
    </Row>
  );

  const items = (record: AdminDTO) => {
    return [
      {
        key: "1",
        label: dropdownItem("Advanced", record, "advanced"),
      },
      {
        key: "2",
        label: dropdownItem("Partner", record, "partner"),
      },
      {
        key: "3",
        label: dropdownItem("Qr de Aeropuerto", record, "airportQrAllowed"),
      },
      {
        key: "4",
        label: dropdownItem("Cartera", record, "wallet"),
      },
      {
        key: "5",
        label: dropdownItem("Coordenadas", record, "isManualEntryAuthorized"),
      },
      {
        key: "6",
        label: dropdownItem("Enviar whatsapp", record, "sendWhatsappMessage"),
      },
      {
        key: "7",
        label: dropdownItem("Mostrar logo en rastreo", record, "showLogo"),
      },
      {
        key: "8",
        label: dropdownItem(
          "Viajes de invitado",
          record,
          "guestTripsEnabled",
        ),
      },
      {
        key: "9",
        label: dropdownItem(
          "Dirección opcional en QR",
          record,
          "qrOptionalAddressAllowed",
        ),
      },
    ];
  };

  const actionItems = (record: AdminDTO) => {
    return [
      {
        key: "1",
        disabled: !record.wallet,
        label: <Text disabled={!record.wallet}>Depositar fondos</Text>,
        onClick: () => {
          if (!record.wallet) return null;
          setIsModalOpen(!isModalOpen);
          setAdminId(record.id);
          form.setFieldsValue([
            {
              name: "amount",
              value: null,
            },
            {
              name: "concept",
              value: AdminWalletConcept,
            },
          ]);
        },
      },
      {
        key: "2",
        label: <Text>Actualizar contraseña</Text>,
        onClick: () => {
          setAdminId(record.id);
          passwordForm.setFieldsValue([
            {
              name: "password",
              value: null,
            },
          ]);
          setShowPasswordInput(!showPasswordInput);
        },
      },
      {
        key: "3",
        label: <Text>Actualizar {`${record.name}`}</Text>,
        onClick: () => {
          navigate(`/admin/update/${record.id}`, {
            replace: true,
          });
        },
      },
      {
        key: "4",
        label: <Text>Iniciar sesión como {`${record.name}`}</Text>,
        onClick: async () =>
          Modal.confirm({
            title: `Ingresar como ${record.name}`,
            content:
              "Al ingresar a otro usuario, si requiere ingresar a su admin, tiene que iniciar sesion de nuevo",
            onOk: async () => {
              await impersonateAdminLogin(record.id);
              navigate("/", { replace: true });
            },
            okText: "Confirmar",
            cancelText: "Cancelar",
          }),
      },
      {
        key: "5",
        label: <Text>Eliminar {`${record.name}`}</Text>,
        onClick: () =>
          Modal.confirm({
            title: `Eliminar ${record.name}`,
            content: "Estas seguro de eliminar este administrador?",
            onOk: async () => {
              await deleteAdmin(record.id);
              setIgnoreFetch(false);
            },
            okText: "Confirmar",
            cancelText: "Cancelar",
          }),
      },
      {
        key: "6",
        label: <Text>Ver Preview de Voucher</Text>,
        onClick: async () => {
          setIsPreviewLoading(true);
          try {
            const previewHtml = await getVoucherPreview(record.id);
            setVoucherPreviewHtml(previewHtml);
            setIsVoucherPreviewOpen(true);
          } catch (error) {
            console.error("Failed to load voucher preview", error);
          } finally {
            setIsPreviewLoading(false);
          }
        },
      },
    ];
  };

  return (
    <ContainerLayout title={title} showNotification={false}>
      <Col span={24}>
        <Form layout="inline">
          <Form.Item>
            <Button
              type="primary"
              icon={<PlusCircleTwoTone />}
              onClick={() =>
                navigate(WebRoutes.CreateAdminRoute, {
                  replace: true,
                })
              }
            >
              Administrador
            </Button>
          </Form.Item>
          <Form.Item>
            <Search
              placeholder="Buscar por nombre"
              allowClear
              onSearch={onSearch}
              loading={loading}
              disabled={loading}
            />
          </Form.Item>
          <Form.Item>
            <Button
              onClick={() =>
                navigate(WebRoutes.AdminDocumentListRoute, {
                  replace: true,
                })
              }
            >
              Lista de liquidaciones
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              loading={isLoadingExcel}
              onClick={async () => await handleExcelExport()}
            >
              Descargar lista
            </Button>
          </Form.Item>
        </Form>
      </Col>
      <Col
        ref={tableContainerRef}
        span={24}
        style={{ width: "100px", height: "83vh", position: "relative" }}
      >
        <Table
          style={{ minWidth: "100%" }}
          rowKey={(record: AdminDTO) => Number(record.id)}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: "max-content", y: "calc(-250px + 97vh)" }}
        >
          <Column title="Id" dataIndex="id" width={100} />
          <Column title="Nombre" width={100} dataIndex="name" />
          <Column title="Correo electronico" dataIndex="email" width={100} />
          <Column title="Movil" dataIndex="mobile" width={100} />
          <Column
            title="Logo"
            width={100}
            render={(record: AdminDTO) =>
              record.picture ? (
                <img
                  className="image-container"
                  src={record.picture}
                  alt="this is a test"
                  style={{ maxWidth: "100px", maxHeight: "100px" }}
                />
              ) : (
                <Space>Sin logo</Space>
              )
            }
          />
          <Column
            title="Tarifa mínima"
            width={100}
            render={(record: AdminDTO) => {
              const minimumTripPrice = Number(record.minimumTripPrice);
              return Number.isFinite(minimumTripPrice)
                ? `$${minimumTripPrice.toFixed(2)}`
                : "Sin asignar";
            }}
          />
          <Column
            title="Activo"
            width={100}
            render={(record: AdminDTO) => {
              return handleIsActive(record.active);
            }}
          />
          <Column
            title="Tipo de Admin"
            width={100}
            render={(record: AdminDTO) => {
              return handleAdminType(record.adminTypeId);
            }}
          />
          <Column
            title="Precios internacionales"
            width={120}
            render={(record: AdminDTO) => {
              return record.international ? "Activo" : "Inactivo";
            }}
          />
          <Column
            title="País"
            width={150}
            render={(record: AdminDTO) => {
              return record.countryName ?? "-";
            }}
          />
          <Column
            title={
              <Tooltip title="El margen de ganancia se aplica al precio final del viaje.">
                <span>Markup (%)</span>
              </Tooltip>
            }
            width={130}
            render={(record: AdminDTO) => {
              return record.markup ?? 20;
            }}
          />
          <Column
            title=""
            width={100}
            render={(record: AdminDTO) => {
              return (
                <Dropdown
                  menu={{
                    items: items(record),
                  }}
                  placement="bottom"
                  arrow
                  trigger={["click"]}
                  getPopupContainer={() =>
                    tableContainerRef.current ?? document.body
                  }
                >
                  <Button>Permisos</Button>
                </Dropdown>
              );
            }}
          />
          <Column
            title=""
            width={100}
            fixed="right"
            render={(record: AdminDTO) => {
              return (
                <Dropdown
                  menu={{
                    items: actionItems(record),
                  }}
                  placement="bottom"
                  arrow
                  trigger={["click"]}
                  getPopupContainer={() =>
                    tableContainerRef.current ?? document.body
                  }
                >
                  <Button>Acciones</Button>
                </Dropdown>
              );
            }}
          />
        </Table>
        <Modal
          title="Actualizar contraseña"
          open={showPasswordInput}
          onOk={passwordForm.submit}
          onCancel={() => setShowPasswordInput(!showPasswordInput)}
          confirmLoading={confirmLoading}
          okText={confirmLoading ? "Cargando" : "Actualizar"}
        >
          <Form
            form={passwordForm}
            name="newPassword"
            onFinish={(e) => handleUpdatePassword(e)}
            layout="vertical"
          >
            <Form.Item
              label="Nueva contraseña"
              name="password"
              rules={[
                {
                  required: true,
                  message: "Por favor inserte una contraseña",
                },
                {
                  pattern: PasswordRegexExpression,
                  message: PasswordRequirementText,
                },
              ]}
              hasFeedback
            >
              <Input.Password />
            </Form.Item>
          </Form>
        </Modal>
        <Modal
          title="Depositar fondos"
          open={isModalOpen}
          onOk={form.submit}
          onCancel={() => setIsModalOpen(false)}
          confirmLoading={confirmLoading}
          okText={confirmLoading ? "Procesando" : "Depositar"}
        >
          <Form
            form={form}
            name="funds"
            onFinish={(e) => handleOk(e)}
            layout="vertical"
          >
            <Form.Item
              label="Cantidad a depositar:"
              name="amount"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input placeholder="Ingresa un numero" type="number" />
            </Form.Item>
            <Form.Item
              label="Concepto de pago:"
              name="concept"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input placeholder="Ingresar concepto" type="text" />
            </Form.Item>
          </Form>
        </Modal>
        <Modal
          title="Preview de Voucher"
          open={isVoucherPreviewOpen}
          footer={null}
          onCancel={() => setIsVoucherPreviewOpen(false)}
          width={800}
        >
          {isPreviewLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>Cargando preview...</div>
          ) : (
            <iframe
              srcDoc={voucherPreviewHtml}
              style={{ width: "100%", height: "600px", border: "none" }}
              title="Voucher Preview"
            />
          )}
        </Modal>
      </Col>
    </ContainerLayout>
  );
};

export default AdminList;
